import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PageLoader } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RiskBadge } from '@/components/common/RiskBadge';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { api, downloadPdf } from '@/lib/api';
import { fmtDate, fmtRel } from '@/lib/format';
import { canValidate, useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { FileText, MessageSquare, ClipboardCheck, RefreshCcw } from 'lucide-react';

const STATUSES = ['Open', 'Assigned', 'Investigation pending', 'Field verification pending', 'Action in progress', 'Monitoring', 'Resolved', 'Closed', 'Reopened'];

export default function IncidentDetailPage() {
  const { incidentId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [inc, setInc] = useState(null);
  const [asset, setAsset] = useState(null);
  const [users, setUsers] = useState([]);
  const [comment, setComment] = useState('');
  const [resolution, setResolution] = useState('');

  const load = async () => {
    const i = await api.get(`/incidents/${incidentId}`).then(r => r.data);
    setInc(i);
    const [a, u] = await Promise.all([
      api.get(`/assets/${i.asset_id}`).then(r => r.data),
      api.get('/users').then(r => r.data).catch(() => []),
    ]);
    setAsset(a); setUsers(u);
  };
  useEffect(() => { load(); }, [incidentId]);

  const patch = async (body) => {
    await api.patch(`/incidents/${incidentId}`, body);
    load();
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/incidents/${incidentId}/comments`, { body: comment });
    setComment('');
    load();
  };

  const closeInc = async () => {
    await api.post(`/incidents/${incidentId}/close`, null, { params: { resolution } });
    toast.success('Incident closed');
    load();
  };

  const reopenInc = async () => {
    await api.post(`/incidents/${incidentId}/reopen`);
    load();
  };

  if (!inc || !asset) return <div className="p-6"><PageLoader /></div>;

  const assignee = users.find(u => u.id === inc.assigned_to);

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <PageHeader
        title={<span className="font-mono">{inc.number}</span>}
        subtitle={<><span>{inc.title}</span> · <Link to={`/app/assets/${asset.id}`} className="hover:text-primary">{asset.name}</Link></>}
        actions={
          <div className="flex items-center gap-2">
            <CategoryBadge category={inc.category} />
            <RiskBadge severity={inc.severity} />
            <Button
              variant="outline"
              data-testid="incident-report-btn"
              onClick={() => downloadPdf(`/reports/incident/${incidentId}/pdf`, `AakashDrishti_Incident_${inc.number || incidentId.slice(0, 8)}.pdf`)}
            >
              <FileText size={14} className="mr-1" /> PDF report
            </Button>
            {inc.assigned_to === user.id && !inc.field_verified && (
              <Button onClick={() => nav(`/app/incidents/${incidentId}/verify`)} data-testid="field-verify-btn"><ClipboardCheck size={14} className="mr-1" /> Field verify</Button>
            )}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-4">
            <div className="text-sm font-semibold">Details</div>
            <p className="mt-2 text-sm">{inc.description}</p>
            {inc.recommended_action && (
              <div className="mt-3 text-xs text-muted-foreground"><b className="text-foreground">Recommended:</b> {inc.recommended_action}</div>
            )}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1.5 text-xs">
              <div className="text-muted-foreground">Status</div><div><Badge variant="outline" className="text-[10px]">{inc.status}</Badge></div>
              <div className="text-muted-foreground">Due</div><div>{fmtDate(inc.due_date)}</div>
              <div className="text-muted-foreground">Assignee</div><div>{assignee?.full_name || '—'}</div>
              <div className="text-muted-foreground">Dept</div><div>{inc.department || '—'}</div>
              <div className="text-muted-foreground">Field verified</div><div>{inc.field_verified ? 'Yes' : 'No'}</div>
              <div className="text-muted-foreground">Created</div><div>{fmtDate(inc.created_at)}</div>
              <div className="text-muted-foreground">Closed</div><div>{fmtDate(inc.closed_at)}</div>
              <div className="text-muted-foreground">Reopens</div><div className="font-mono">{inc.reopened_count}</div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2">
              <MessageSquare size={14} />
              <div className="text-sm font-semibold">Comments & activity</div>
            </div>
            <div className="mt-3 space-y-3" data-testid="incident-comments">
              {(inc.comments || []).map(c => (
                <div key={c.id} className="text-sm">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium">{c.user_name || c.user_id}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">{fmtRel(c.created_at)}</span>
                  </div>
                  <div className="text-muted-foreground mt-0.5">{c.body}</div>
                </div>
              ))}
              {(inc.comments || []).length === 0 && <div className="text-xs text-muted-foreground">No comments yet.</div>}
            </div>
            <div className="mt-4 flex gap-2">
              <Textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment…" data-testid="incident-comment-input" />
              <Button onClick={addComment} disabled={!comment.trim()} data-testid="incident-comment-submit">Post</Button>
            </div>
          </Card>

          {inc.field_verification && (
            <Card className="p-4" data-testid="field-verification-details">
              <div className="text-sm font-semibold">Field verification result</div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <div className="text-muted-foreground">Verified</div><div>{inc.field_verification.verified ? 'Yes' : 'No'}</div>
                <div className="text-muted-foreground">Action taken</div><div>{inc.field_verification.action_taken || '—'}</div>
                <div className="text-muted-foreground">Notes</div><div>{inc.field_verification.notes || '—'}</div>
                <div className="text-muted-foreground">GPS</div><div className="font-mono">{inc.field_verification.gps_lat}, {inc.field_verification.gps_lng}</div>
                <div className="text-muted-foreground">Time</div><div>{fmtDate(inc.field_verification.verified_at)}</div>
              </div>
            </Card>
          )}

          {(inc.attachments || []).length > 0 && (
            <Card className="p-4">
              <div className="text-sm font-semibold">Attachments ({inc.attachments.length})</div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {inc.attachments.map(a => (
                  <div key={a.id} className="rounded-lg overflow-hidden border border-border">
                    <img src={`data:${a.mime_type};base64,${a.file_b64}`} alt={a.file_name} className="w-full h-32 object-cover" />
                    <div className="p-1.5 text-[10px] text-muted-foreground truncate">{a.file_name}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          {canValidate(user.role) && (
            <Card className="p-4">
              <div className="text-sm font-semibold">Workflow controls</div>
              <div className="mt-3 space-y-2">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={inc.status} onValueChange={s => patch({ status: s })}>
                    <SelectTrigger data-testid="incident-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Assign to</Label>
                  <Select value={inc.assigned_to || ''} onValueChange={a => patch({ assigned_to: a })}>
                    <SelectTrigger data-testid="incident-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Department</Label>
                  <Input value={inc.department || ''} onChange={e => setInc({ ...inc, department: e.target.value })}
                         onBlur={() => patch({ department: inc.department })} />
                </div>
                <div className="pt-2 border-t border-border">
                  <Label className="text-xs">Resolution notes</Label>
                  <Textarea rows={2} value={resolution} onChange={e => setResolution(e.target.value)} placeholder="e.g., site inspected, no action needed" />
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {inc.status !== 'Closed' ? (
                      <Button size="sm" onClick={closeInc} data-testid="incident-close-btn">Close incident</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={reopenInc} data-testid="incident-reopen-btn"><RefreshCcw size={14} className="mr-1" /> Reopen</Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
